import * as React from 'react';
import { Alert, Button, Divider, Input, Select, Space, Switch, Typography, Popconfirm } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons';

import { BetaRelationType, ClientFeatureState } from '@frontend/app/types/globalTypes';
import { IClientFeature, useGetClientFeatures } from '@frontend/app/hooks';
import {
  IClientFeatureBeta,
  useGetClientFeatureBetas,
  useSaveClientFeatureBeta,
  useDeleteClientFeatureBeta,
  useReorderClientFeatureBetas,
} from '@frontend/app/hooks/clientFeatures';

const { useState, useEffect, useMemo } = React;
const { Option } = Select;
const { Title, Text } = Typography;

interface IRelationRow {
  relatedFeatureId: number | undefined;
  relationType: BetaRelationType;
}

const RELATION_LABELS: Record<BetaRelationType, string> = {
  [BetaRelationType.PREREQ]: 'Prerequisite',
  [BetaRelationType.PAIR]: 'Pair',
  [BetaRelationType.ENABLE_ONLY]: 'Enable-only',
};

// Shown above the related-flag rows so the short option labels have their meaning nearby.
const RELATION_HELP: Record<BetaRelationType, string> = {
  [BetaRelationType.PREREQ]: 'the beta is hidden unless the client already has this flag',
  [BetaRelationType.PAIR]: 'the flag turns on and off together with the beta',
  [BetaRelationType.ENABLE_ONLY]: 'the flag turns on with the beta, but stays on when the beta is turned off',
};

interface IProps {
  /** The feature being edited. A beta is built on this flag. */
  feature: IClientFeature;
}

/**
 * The "Beta" section of the feature edit drawer: promote a client feature to the data-driven
 * Beta Features page. Own save (saveClientFeatureBeta) — separate from the feature's own save.
 */
export const BetaConfigSection: React.FC<IProps> = ({ feature }) => {
  const { betas, loading } = useGetClientFeatureBetas();
  const { features: allFeatures, loading: featuresLoading } = useGetClientFeatures();
  const { saveClientFeatureBeta, loading: saving } = useSaveClientFeatureBeta();
  const { deleteClientFeatureBeta, loading: deleting } = useDeleteClientFeatureBeta();
  const { reorderClientFeatureBetas, loading: reordering } = useReorderClientFeatureBetas();

  const existing: IClientFeatureBeta | undefined = useMemo(
    () => betas.find((beta) => beta.clientFeatureId === feature?.id),
    [betas, feature],
  );

  const [isBeta, setIsBeta] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [relations, setRelations] = useState<IRelationRow[]>([]);

  // Hydrate only when the feature (or its persisted beta) changes — NOT on every `betas` refresh.
  // The list query is cache-and-network, so it can refetch mid-edit; re-running hydration then
  // would wipe the admin's unsaved title/description/sortOrder/relations. Key on the identities we
  // hydrate from and skip if we've already hydrated that exact beta/feature.
  const hydratedKeyRef = React.useRef<string | null>(null);
  useEffect(() => {
    const key = `${feature?.id ?? 'none'}:${existing?.id ?? 'new'}`;
    if (hydratedKeyRef.current === key) {
      return;
    }
    hydratedKeyRef.current = key;
    if (existing) {
      setIsBeta(true);
      setTitle(existing.title);
      setDescription(existing.description ?? '');
      setSortOrder(existing.sortOrder ?? 0);
      setRelations(
        (existing.relations ?? []).map((relation) => ({
          relatedFeatureId: relation.relatedFeatureId,
          relationType: relation.relationType,
        })),
      );
    } else {
      setIsBeta(false);
      setTitle(feature?.name ?? '');
      setDescription(feature?.description ?? '');
      // Default a new beta to the end of the list so the admin never has to guess a number.
      const maxOrder = betas.reduce((max, beta) => Math.max(max, beta.sortOrder), -1);
      setSortOrder(maxOrder + 1);
      setRelations([]);
    }
  }, [existing, feature, betas]);

  const relatedOptions = useMemo(
    () => (allFeatures ?? []).filter((candidate) => candidate.id !== feature?.id),
    [allFeatures, feature],
  );

  // A related flag whose client_feature row has been soft-deleted no longer comes back from
  // getClientFeatures, so its Select renders blank and the rule is invisible — the admin cannot
  // tell a broken rule from an unset one. Detect it against the FULL feature list (not
  // relatedOptions, which drops the beta's own flag) so a self-referential row isn't mislabelled.
  //
  // This matters most for `prereq`: a prereq pointing at a deleted flag is unsatisfiable, and the
  // page hides the beta from every client until it is fixed. Enable-only/pair rules just carry a
  // dead id, which is inert.
  const knownFeatureIds = useMemo(() => new Set((allFeatures ?? []).map((f) => f.id)), [allFeatures]);

  const isRelationFlagMissing = (relatedFeatureId: number | undefined): boolean =>
    // Nothing is "missing" until the list has actually loaded, or every row would flash a warning.
    !featuresLoading && knownFeatureIds.size > 0 && relatedFeatureId != null && !knownFeatureIds.has(relatedFeatureId);

  const missingRelations = relations.filter((row) => isRelationFlagMissing(row.relatedFeatureId));
  const hasMissingPrereq = missingRelations.some((row) => row.relationType === BetaRelationType.PREREQ);

  // All betas in the order they'll appear on the page — shown as a reference so the admin can see
  // where each one sits and pick a sensible position, rather than entering a number blind.
  const orderedBetas = useMemo(
    () => [...betas].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id),
    [betas],
  );

  // Drag-to-reorder the page order. Reordering is an ordering-only write
  // (reorderClientFeatureBetas → resequence sortOrder): it never re-saves a beta's config, so it
  // can't clobber a title/relations edit made concurrently by another admin, and it works even for
  // a GA-flag beta (which saveClientFeatureBeta rejects). The dragged beta is tracked by *id*, not
  // list index, so a mid-drag refetch of `betas` can't reorder the wrong row. Reorder acts on the
  // saved betas — a not-yet-saved beta joins the order once it's saved.
  const [dragId, setDragId] = useState<number | null>(null);

  const persistOrder = (orderedIds: number[]) => {
    reorderClientFeatureBetas({ variables: { orderedIds } });
  };

  // Drop the dragged beta into the slot currently held by the beta at `targetIndex`.
  const onReorderDrop = (targetIndex: number) => {
    const id = dragId;
    setDragId(null);
    if (id === null) {
      return;
    }
    const fromIndex = orderedBetas.findIndex((beta) => beta.id === id);
    if (fromIndex === -1 || fromIndex === targetIndex) {
      return;
    }
    const ids = orderedBetas.filter((beta) => beta.id !== id).map((beta) => beta.id);
    ids.splice(Math.min(targetIndex, ids.length), 0, id);
    persistOrder(ids);
  };

  // Keyboard / touch fallback for reordering (the drag handles have no non-pointer path).
  const moveBeta = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= orderedBetas.length) {
      return;
    }
    const ids = orderedBetas.map((beta) => beta.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    persistOrder(ids);
  };

  const addRelation = () =>
    setRelations((prev) => [...prev, { relatedFeatureId: undefined, relationType: BetaRelationType.PREREQ }]);

  const updateRelation = (index: number, patch: Partial<IRelationRow>) =>
    setRelations((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeRelation = (index: number) => setRelations((prev) => prev.filter((_, i) => i !== index));

  const onSave = () => {
    saveClientFeatureBeta({
      variables: {
        beta: {
          ...(existing ? { id: existing.id } : {}),
          clientFeatureId: feature.id,
          title,
          description,
          sortOrder,
          relations: relations
            .filter((row) => row.relatedFeatureId != null)
            .map((row) => ({ relatedFeatureId: row.relatedFeatureId as number, relationType: row.relationType })),
        },
      },
    });
  };

  const onRemoveBeta = () => {
    if (existing) {
      deleteClientFeatureBeta({ variables: { id: existing.id } });
    }
    setIsBeta(false);
  };

  // Turning the switch off must not just hide the form while the config stays persisted (it would
  // reappear next open). If a config exists, toggling off removes it so the switch reflects reality.
  const onToggleBeta = (checked: boolean) => {
    if (checked) {
      setIsBeta(true);
    } else {
      onRemoveBeta();
    }
  };

  if (!feature?.id) {
    return <Text type="secondary">Save the feature first, then configure its beta.</Text>;
  }

  // Once a flag is globally ENABLED (GA), evaluateFeatureState ignores the per-client opt-in, so
  // the beta's on/off toggle can no longer turn the flag off — disabling it is a silent no-op. A
  // beta only makes sense while the flag is still limited/opt-in, so warn the admin to remove it.
  const isGloballyEnabled = feature.state === ClientFeatureState.ENABLED;

  return (
    <div>
      <Divider />
      {isGloballyEnabled && (existing || isBeta) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This flag is globally enabled (GA)"
          description="A GA flag is on for every client regardless of the per-client toggle, so the beta's on/off switch can no longer turn it off — disabling the beta here does nothing. Betas are for flags that are still limited/opt-in; remove this beta now that the flag has shipped."
        />
      )}
      <Space direction="horizontal" align="center">
        <Switch checked={isBeta} loading={loading || deleting} onChange={onToggleBeta} />
        <Title level={5} style={{ margin: 0 }}>
          Show on the Beta Features page
        </Title>
      </Space>

      {isBeta && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Title</Text>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Recruit Hub" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Description</Text>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Shown on the beta card"
              rows={3}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Text strong>Display order on the Beta Features page</Text>{' '}
            <Text type="secondary">(drag, or use the arrows, to reorder; top = higher up)</Text>
            <div style={{ marginTop: 8 }}>
              {orderedBetas.map((beta, index) => {
                const isCurrent = beta.clientFeatureId === feature.id;
                return (
                  <div
                    key={beta.id}
                    draggable={!reordering}
                    onDragStart={() => setDragId(beta.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onReorderDrop(index)}
                    onDragEnd={() => setDragId(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '4px 10px',
                      marginBottom: 4,
                      borderRadius: 4,
                      border: '1px solid #f0f0f0',
                      background: isCurrent ? '#e6f7ff' : '#fafafa',
                      cursor: reordering ? 'default' : 'grab',
                      opacity: dragId === beta.id ? 0.4 : 1,
                    }}
                  >
                    <MenuOutlined style={{ color: '#bfbfbf' }} />
                    <Text style={{ flex: 1, fontWeight: isCurrent ? 600 : 400 }}>
                      {beta.title}{' '}
                      {/* The flag each beta is built on. Titles are admin-written prose and can be
                          similar or renamed; the flag name is what the row actually maps to. */}
                      <Text type="secondary" style={{ fontWeight: 400 }}>
                        [{beta.clientFeatureName}]
                      </Text>
                      {isCurrent ? ' (this one)' : ''}
                    </Text>
                    <Button
                      size="small"
                      type="text"
                      icon={<ArrowUpOutlined />}
                      aria-label={`Move ${beta.title} up`}
                      disabled={reordering || index === 0}
                      onClick={() => moveBeta(index, -1)}
                    />
                    <Button
                      size="small"
                      type="text"
                      icon={<ArrowDownOutlined />}
                      aria-label={`Move ${beta.title} down`}
                      disabled={reordering || index === orderedBetas.length - 1}
                      onClick={() => moveBeta(index, 1)}
                    />
                  </div>
                );
              })}
              {!orderedBetas.some((beta) => beta.clientFeatureId === feature.id) && (
                <div style={{ padding: '6px 10px', color: '#8c8c8c', fontStyle: 'italic' }}>
                  {title || feature.name} — save the beta to add it to the order
                </div>
              )}
            </div>
          </div>

          <Divider orientation="left" plain>
            Related flags
          </Divider>
          <div style={{ marginBottom: 12 }}>
            {Object.values(BetaRelationType).map((mode) => (
              <Text key={mode} type="secondary" style={{ fontSize: 12, display: 'block' }}>
                <b>{RELATION_LABELS[mode]}</b> — {RELATION_HELP[mode]}
              </Text>
            ))}
            {/* The fan-out lives in the beta toggle path (buildBetaToggleFlags), not in the flag
                writes themselves — so a flag set by hand never drags its related flags along. */}
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
              <b>Applies to the Beta Features toggle only.</b> These flags are fanned out when a client
              turns this beta on or off on the Beta Features page. Setting a flag by hand — here in
              admin, or anywhere else that writes client features directly — does not touch related
              flags.
            </Text>
          </div>
          {missingRelations.length > 0 && (
            <Alert
              type={hasMissingPrereq ? 'error' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
              message={
                missingRelations.length === 1
                  ? 'A related flag no longer exists'
                  : `${missingRelations.length} related flags no longer exist`
              }
              description={
                hasMissingPrereq
                  ? 'A prerequisite below points at a deleted flag, so it can never be satisfied — this beta is currently hidden from every client. Repoint it at a live flag, or remove the rule, to bring the beta back.'
                  : 'A rule below points at a deleted flag. It has no effect and the row shows an empty flag; repoint or remove it.'
              }
            />
          )}
          {relations.map((row, index) => {
            const missing = isRelationFlagMissing(row.relatedFeatureId);
            return (
            <Space key={index} align="start" style={{ display: 'flex', marginBottom: 8 }}>
              <Select
                showSearch
                style={{ width: 220 }}
                placeholder="Flag"
                optionFilterProp="children"
                status={missing ? 'error' : undefined}
                value={row.relatedFeatureId}
                onChange={(value) => updateRelation(index, { relatedFeatureId: value })}
              >
                {relatedOptions.map((candidate) => (
                  <Option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </Option>
                ))}
              </Select>
              <Select
                style={{ width: 340 }}
                value={row.relationType}
                onChange={(value) => updateRelation(index, { relationType: value })}
              >
                {Object.values(BetaRelationType).map((mode) => (
                  <Option key={mode} value={mode}>
                    {RELATION_LABELS[mode]}
                  </Option>
                ))}
              </Select>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeRelation(index)} />
              {missing && (
                <Text type="danger" style={{ fontSize: 12, lineHeight: '32px' }}>
                  flag #{row.relatedFeatureId} was deleted
                </Text>
              )}
            </Space>
            );
          })}
          <Button type="dashed" icon={<PlusOutlined />} onClick={addRelation} style={{ marginTop: 4 }}>
            Add related flag
          </Button>

          <div style={{ marginTop: 20 }}>
            <Space>
              <Button
                type="primary"
                loading={saving}
                disabled={!title || isGloballyEnabled}
                onClick={onSave}
              >
                Save beta
              </Button>
              {existing && (
                <Popconfirm title="Remove this beta from the Beta Features page?" onConfirm={onRemoveBeta}>
                  <Button danger loading={deleting}>
                    Remove beta
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};
